import type { CaptureResult } from 'posthog-js';

// Keeps secrets and personal data carried in URLs (reset and verification tokens,
// OAuth code/state, invite codes, usernames) out of analytics. The SDK copies the
// page URL into many properties, so these would otherwise reach PostHog.
//
// Two layers, since each misses something:
// - `sanitizeCaptureResult` (`before_send`) rewrites every outgoing event, but runs
//   after the SDK has persisted the landing URL.
// - `SENSITIVE_QUERY_PARAMS` feeds the SDK's own masking, the only thing that reaches
//   the persisted copy, but it masks query params by name only.
//
// ENG-724 tracks removing secrets from URLs; this is containment until then.

/**
 * Query parameters carrying a secret or personal data, for the SDK's own masking.
 *
 * `sanitizeCaptureResult` drops every query parameter, so it doesn't rely on this list.
 */
export const SENSITIVE_QUERY_PARAMS = [
  // Password reset and email verification links.
  'token',
  // OAuth provider callback.
  'code',
  'state',
  // OAuth identity forwarded from the callback to registration.
  'provider_user_id',
  'provider_email',
  'provider_name',
  'provider_picture',
];

/**
 * Route prefixes whose following path segments identify a secret or a person.
 *
 * Everything after the prefix becomes the placeholder. Applied to this app's URLs only.
 */
const SENSITIVE_PATH_PREFIXES: readonly { prefix: string; placeholder: string }[] = [
  { prefix: '/register', placeholder: ':invite' },
  { prefix: '/citizen', placeholder: ':username' },
];

/**
 * Absolute http(s) URLs embedded anywhere in a string, such as an exception message.
 *
 * Ends only at whitespace or a quote: stopping at the `<` of `SDK_MASK_MARKER` would
 * leave the rest of the URL, fragment included, unsanitized.
 */
const EMBEDDED_URL = /https?:\/\/[^\s"']+/g;

/**
 * Placeholder the SDK's masking writes over a query parameter.
 *
 * It can swallow the `?`, landing in what parses as the path, so URLs are cut here
 * before parsing.
 */
const SDK_MASK_MARKER = '<masked>';

/**
 * A link target inside an autocapture `$elements_chain`.
 *
 * Attributes are `key="value"` pairs after a `:` or the previous closing quote, with
 * quotes escaped as `\"`; a link appears as both `href` and `attr__href`. Groups:
 * boundary, key, escaped value.
 *
 * The closing quote is a lookahead because it is also the next pair's boundary;
 * consuming it would let an adjacent `href` through.
 */
const CHAIN_HREF = /(^|[:"])(attr__href|href)="((?:[^"\\]|\\.)*)(?=")/g;

/** Replace the identifying tail of a sensitive route with its placeholder. */
function redactPath(pathname: string): string {
  for (const { prefix, placeholder } of SENSITIVE_PATH_PREFIXES) {
    if (pathname.startsWith(`${prefix}/`) && pathname.length > prefix.length + 1) {
      return `${prefix}/${placeholder}`;
    }
  }
  return pathname;
}

/**
 * Reduce a URL or link target to the parts that are safe to record.
 *
 * Drops the query and fragment, and redacts sensitive paths on this app's URLs.
 * Relative input stays relative; non-web schemes (`mailto:`) keep only the scheme;
 * unparseable input is returned unchanged.
 */
function sanitizeUrl(value: string, appOrigin: string): string {
  const unmasked = value.split(SDK_MASK_MARKER)[0];
  let parsed: URL;
  try {
    parsed = new URL(unmasked, appOrigin);
  } catch {
    return value;
  }

  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return parsed.protocol;
  }

  const isAppUrl = parsed.origin === appOrigin;
  const pathname = isAppUrl ? redactPath(parsed.pathname) : parsed.pathname;
  const isAbsolute = /^[a-z][a-z\d+.-]*:/i.test(value);
  return isAbsolute ? `${parsed.origin}${pathname}` : pathname;
}

/**
 * Sanitize one property value according to what its key says it holds.
 *
 * - `*pathname`: a bare path, with no scheme to recognize it by.
 * - `attr__href`: a link target as written, often relative.
 * - `$elements_chain`: link targets inside a serialized element list.
 * - Anything else: embedded absolute URLs are sanitized in place.
 */
function sanitizeValue(key: string, value: unknown, appOrigin: string): unknown {
  if (typeof value === 'string') {
    if (key.endsWith('pathname')) {
      return redactPath(value);
    }
    if (key === 'attr__href') {
      return sanitizeUrl(value, appOrigin);
    }
    if (key === '$elements_chain') {
      return value.replace(
        CHAIN_HREF,
        (_match, boundary: string, attribute: string, href: string) =>
          `${boundary}${attribute}="${sanitizeUrl(href, appOrigin)}`
      );
    }
    return value.replace(EMBEDDED_URL, (url) => sanitizeUrl(url, appOrigin));
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeValue(key, item, appOrigin));
  }

  if (value !== null && typeof value === 'object') {
    return sanitizeProperties(value as Record<string, unknown>, appOrigin);
  }

  return value;
}

/** Sanitize every value in a property bag, recursing into nested objects and arrays. */
function sanitizeProperties(
  properties: Record<string, unknown>,
  appOrigin: string
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(properties).map(([key, value]) => [key, sanitizeValue(key, value, appOrigin)])
  );
}

/**
 * PostHog `before_send` hook that strips URL-borne secrets and personal data.
 *
 * Rewrites event and person properties. `appOrigin` identifies this app's URLs; it
 * defaults to the page's origin and is a parameter for tests.
 */
export function sanitizeCaptureResult(
  result: CaptureResult | null,
  appOrigin: string = window.location.origin
): CaptureResult | null {
  if (!result) {
    return result;
  }

  return {
    ...result,
    properties: sanitizeProperties(result.properties, appOrigin),
    ...(result.$set ? { $set: sanitizeProperties(result.$set, appOrigin) } : {}),
    ...(result.$set_once ? { $set_once: sanitizeProperties(result.$set_once, appOrigin) } : {}),
  };
}
